import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { findWhere } from "underscore";
import { Button } from "reactstrap";

import { headerOpenAtom, toggleHeaderOpenAtom } from "../../atoms/PageAtoms";
import useDuplicateView from "../../api/api-hooks/views/useDuplicateView";
import useWorkspacePermissions from "../../hooks/useWorkspacePermissions";
import ShareScreenDrawer from "./ShareScreenDrawer/ShareScreenDrawer";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import useDeleteView from "../../api/api-hooks/views/useDeleteView";
import useUpdatePage from "../../api/api-hooks/pages/useUpdatePage";
import { ViewTypesWithField } from "./AddViewDrawer/AddViewDrawer";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import usePagePermissions from "../../hooks/usePagePermissions";
import PopoverDropdownMenu from "../Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "../Menus/DropdownMenuDivider";
import { isGlobalTenantAtom } from "../../atoms/GlobalAtoms";
import { Header, HeaderOptions } from "./PageViewHeader";
import usePage from "../../api/api-hooks/pages/usePage";
import { currentViewAtom } from "../../atoms/ViewAtoms";
import { PageView } from "../../types/GlobalInterfaces";
import DotsOptionsMenu from "../Menus/DotsOptionsMenu";
import ViewSettingsDrawer from "./ViewSettingsDrawer";
import { View } from "../../types/PageInterfaces";
import AppNavbar from "../AppNavbar/AppNavbar";
import StrUtils from "../../utils/StrUtils";
import { IsMobileAtom } from "../../App";

const Options = (
  view: PageView,
  startEditing: any,
  setShareViewID: any,
  handleDuplicateView: any,
  setReorderAppNavbar: any,
  handleDeleteView: any,
  cPermissions: any,
  wPermissions: any,
  pPermissions: any
) => {
  const isMobile = useAtomValue(IsMobileAtom);

  const hasSettings = ![
    "Summary",
    "History",
    ...ViewTypesWithField,
    "External",
  ].includes(view?.Type);

  return (
    <PopoverDropdownMenu>
      {!isMobile && (
        <DropdownOptionWithIcon
          name="Reorder Views"
          onClick={() => setReorderAppNavbar(true)}
          icon="mdi mdi-reorder-vertical"
          disabled={
            !cPermissions.cloud_modify_page &&
            !wPermissions.workspace_modify_page &&
            !pPermissions.page_modify_view
          }
        />
      )}
      {hasSettings && !isMobile && (
        <DropdownOptionWithIcon
          icon="mdi mdi-share-variant"
          onClick={() => {
            setShareViewID(view?.id);
          }}
          name="Share View"
        />
      )}
      {hasSettings && (
        <DropdownOptionWithIcon
          name="View Settings"
          onClick={() => startEditing(view)}
          icon="mdi mdi-cog"
          disabled={
            !cPermissions.cloud_modify_page &&
            !wPermissions.workspace_modify_page &&
            !pPermissions.page_modify_view
          }
        />
      )}

      {hasSettings && view?.Type !== "Calendar" && !isMobile && (
        <DropdownOptionWithIcon
          name="Duplicate View"
          onClick={() => handleDuplicateView({ viewID: view.id })}
          icon="mdi mdi-content-copy"
          disabled={
            !cPermissions.cloud_modify_page &&
            !wPermissions.workspace_modify_page &&
            !pPermissions.page_create_duplicate_view
          }
        />
      )}

      {!isMobile && (
        <>
          <DropdownMenuDivider />
          <DropdownOptionWithIcon
            name="Delete View"
            onClick={() => handleDeleteView({ viewID: view.id })}
            icon="mdi mdi-delete-outline"
            disabled={
              !cPermissions.cloud_modify_page &&
              !wPermissions.workspace_modify_page &&
              !pPermissions.page_delete_view
            }
          />
        </>
      )}
    </PopoverDropdownMenu>
  );
};

const PageViewNavbar = ({
  view,
  tenantID,
  workspaceID,
  pageID,
  setAddViewDrawer,
}: {
  view: View | null;
  tenantID: string;
  workspaceID: string;
  pageID: string;
  setAddViewDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { page, pageLoading } = usePage(pageID);
  const { updatePage } = useUpdatePage();
  const { duplicateView } = useDuplicateView(pageID);
  const { deleteView } = useDeleteView(pageID, view?._id);
  const setCurrentView = useSetAtom(currentViewAtom);
  const isMobile = useAtomValue(IsMobileAtom);
  const isGlobalTenant = useAtomValue(isGlobalTenantAtom);

  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [editView, setEditView] = useState<PageView | null>(null);
  const [shareViewID, setShareViewID] = useState("");

  const [headerOpen] = useAtom(headerOpenAtom);
  const toggleHeaderOpen = useSetAtom(toggleHeaderOpenAtom);
  const navigate = useNavigate();
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions(pageID);

  const isInstalledApp = !!page?.GlobalAppID && !isGlobalTenant;

  const startEditing = (viewTab: PageView) => {
    setEditView(viewTab);
  };

  const onSelect = (viewID: string) => {
    const selectedView = page?.Views?.find?.((v: any) => v.id === viewID);

    if (selectedView) setCurrentView(selectedView);

    navigate(
      `/clouds/${tenantID}/workspaces/${workspaceID}/pages/${pageID}/views/${viewID}`
    );
  };

  const tabs = useMemo(() => {
    if (!Array.isArray(page?.Views) || page?.Views?.length === 0) {
      return [];
    }

    const views = page?.Views.filter((p: PageView) => !p?.Hide);

    return views?.map((tab: PageView) => ({
      title: (
        <>
          <div className="tw:inline-block">
            {tab.Type === "External" && (
              <i
                className="mdi mdi-open-in-new pr-2"
                style={{ position: "relative", top: 1, fontSize: 15 }}
              />
            )}
            {tab?.Shared === true && (
              <i
                className="mdi mdi-share-variant tw:text-primary tw:pr-2"
                style={{ position: "relative", top: 1, fontSize: 15 }}
              />
            )}
            {tab.Name}
          </div>
          {!isInstalledApp && (
            <DotsOptionsMenu
              id={tab.id}
              // @ts-ignore
              options={Options}
              optionsProps={[
                tab,
                startEditing,
                setShareViewID,
                duplicateView,
                setIsEditingOrder,
                deleteView,
                cPermissions,
                wPermissions,
                pPermissions,
              ]}
            />
          )}
        </>
      ),
      link: null,
      activeValue: tab?.id,
      externalLink: tab?.Link ?? null,
    }));
  }, [page?.Views, pPermissions, cPermissions, wPermissions]);

  if (pageLoading)
    return (
      <div className="tw:flex tw:mt-4">
        <div className="skeleton mr-2" style={{ width: 150, height: 30 }}></div>
        <div className="skeleton mr-2" style={{ width: 150, height: 30 }}></div>
        <div className="skeleton mr-2" style={{ width: 150, height: 30 }}></div>
        <div className="skeleton mr-2" style={{ width: 150, height: 30 }}></div>
      </div>
    );

  return (
    <div
      className={`${
        !headerOpen && "tw:pt-1"
      } tw:flex tw:items-center tw:relative`}
    >
      <div className="tw:flex tw:w-full tw:sm:w-[95%]">
        {!headerOpen && (
          <div className="tw:flex tw:items-center">
            <Header page={page} />
          </div>
        )}
        <div style={{ width: headerOpen ? "100%" : "80%" }}>
          <AppNavbar
            tabs={tabs}
            activeTab={view?.id}
            onSelect={(viewID: string) => onSelect(viewID)}
            dynamic={pPermissions.page_create_view}
            onAdd={() => setAddViewDrawer(true)}
            isEditingOrder={isEditingOrder}
            setIsEditingOrder={setIsEditingOrder}
            fontSize="sm"
            onReorder={(tabIDArr: any[]) => {
              let newViews = tabIDArr?.map((id) => {
                return findWhere(page?.Views, { id });
              });

              let newPage = structuredClone(page);
              newPage.Views = structuredClone(newViews);

              updatePage({
                pageID: page?.id,
                payload: {
                  payload: { Views: newViews },
                  _UserActivity: {
                    // @ts-ignore
                    WorkspaceID: workspaceID,
                    Activity: {
                      id: StrUtils.uuidv4(),
                      Type: "edited-page",
                      Page: {
                        id: page.id,
                        Name: page.Name,
                        Logo: page.Icon,
                      },
                    },
                  },
                },
              });
            }}
          />
        </div>
      </div>
      {!headerOpen && (
        <div style={{ right: 40, bottom: 6 }}>
          <div className="tw: absolute tw:md:inline-block tw:hidden">
            <HeaderOptions page={page} />
          </div>
        </div>
      )}
      {!isMobile && (
        <div className="tw:absolute tw:right-0 tw:bottom-1.5">
          <Button
            size="sm"
            outline
            color="secondary"
            className="tw:w-8 tw:h-8 tw:p-0 tw:mb-0"
            onClick={(e) => {
              e.preventDefault();
              toggleHeaderOpen();
            }}
          >
            <i
              className={`mdi mdi-chevron-${
                headerOpen ? "up" : "down"
              } tw:text-xl`}
            />
          </Button>
        </div>
      )}
      <ViewSettingsDrawer
        pageID={pageID}
        viewID={editView?.id}
        onClose={() => setEditView(null)}
        isReusedView={!!editView?.ReusableView}
      />
      <ShareScreenDrawer
        open={!!shareViewID}
        onClose={() => setShareViewID("")}
        viewID={shareViewID}
        recordID={undefined}
        recordModule={undefined}
        pageID={pageID}
        documentID={undefined}
      />
    </div>
  );
};

export default PageViewNavbar;
